import React from 'react';
import { Briefcase, Calendar, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const experiences = [
  {
    id: 1,
    title: "Cloud Intern",
    company: "Spy Agent",
    period: "Apr 2025 – Jun 2025",
    location: "Remote",
    description: "Contributed to cloud infrastructure deployments, automation tasks, and DevOps workflows. Gained practical, industry-aligned hands-on experience with cloud platforms and modern DevOps practices, working on real-world cloud deployments and infrastructure automation.",
    technologies: ["AWS", "Cloud Infrastructure", "DevOps", "Automation"]
  },
  {
    id: 2,
    title: "Google Cloud Arcade – Cohort 1",
    company: "Google Cloud Arcade 2025",
    period: "Jan 2025 – Jun 2025",
    location: "Remote",
    description: "Gained hands-on experience with Google Cloud through guided labs, challenges, and real-world cloud tasks. Worked with Compute Engine, IAM, Artifact Registry, Cloud Run, and Kubernetes Engine. Strengthened understanding of cloud-native deployments, serverless functions, and monitoring.",
    technologies: ["GCP", "Cloud-Native", "Kubernetes", "Serverless", "Hands-on Labs"]
  },
  {
    id: 3,
    title: "Google Cloud Arcade – Cohort 2",
    company: "Google Cloud Arcade 2025",
    period: "Jun 2025 – Dec 2025",
    location: "Remote",
    description: "Advanced hands-on experience through real-world GCP labs covering Cloud Functions, VPC, Cloud Monitoring, GKE, Cloud SQL, and DevOps workflows. Focused on practical implementation, automation, and multi-cloud readiness.",
    technologies: ["GCP", "Cloud Engineering", "Monitoring", "Automation", "GKE"]
  }
];

const ExperienceSection = () => {
  return (
    <section id="experience" className="min-h-screen flex items-center justify-center py-20 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Work <span className="text-cyan-400">Experience</span>
          </h2>
          <p className="text-gray-400 text-lg mb-4">My professional journey in Cloud & DevOps</p>
          <div className="flex justify-center">
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full"></div>
          </div>
        </motion.div>

        {/* Timeline Container */}
        <div className="max-w-5xl mx-auto relative">
          {/* Vertical Timeline Line */}
          <div className="absolute left-0 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-blue-500 to-purple-600 opacity-50"></div>

          {/* Glowing effect on timeline */}
          <div className="absolute left-0 md:left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400/30 via-blue-500/30 to-purple-600/30 blur-sm"></div>

          {/* Experience Items */}
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                className="relative pl-12 md:pl-24"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                {/* Timeline Node */}
                <div className="absolute left-0 md:left-8 top-8 transform -translate-x-1/2">
                  {/* Outer glow ring */}
                  <div className="absolute inset-0 w-6 h-6 bg-cyan-400/30 rounded-full blur-md animate-pulse"></div>

                  {/* Middle ring */}
                  <div className="absolute inset-0 w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full opacity-60"></div>

                  {/* Inner dot */}
                  <div className="relative w-6 h-6 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full border-4 border-gray-900 shadow-lg shadow-cyan-500/50"></div>

                  {/* Icon in center */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Experience Card */}
                <motion.div
                  className="bg-gradient-to-br from-gray-900/70 to-gray-800/50 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-6 md:p-8 shadow-2xl hover:border-cyan-400/50 hover:shadow-cyan-500/10 transition-all duration-300 group"
                  whileHover={{ scale: 1.02, y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Card Header */}
                  <div className="flex items-start gap-4 mb-4">
                    {/* Icon */}
                    <div className="p-3 bg-cyan-400/10 rounded-xl group-hover:bg-cyan-400/20 transition-colors duration-300 flex-shrink-0">
                      <Briefcase className="text-cyan-400" size={28} />
                    </div>

                    {/* Title and Company */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">
                        {exp.title}
                      </h3>
                      <p className="text-cyan-400 font-semibold text-lg">{exp.company}</p>
                    </div>
                  </div>

                  {/* Date and Location Badges */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/60 rounded-full text-sm text-gray-300 border border-gray-700/50">
                      <Calendar size={16} className="text-cyan-400" />
                      <span>{exp.period}</span>
                    </div>
                    {exp.location && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/60 rounded-full text-sm text-gray-300 border border-gray-700/50">
                        <MapPin size={16} className="text-cyan-400" />
                        <span>{exp.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-300 leading-relaxed mb-5">
                    {exp.description}
                  </p>

                  {/* Technology Tags */}
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 text-cyan-400 rounded-full text-sm font-medium hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-all duration-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Decorative corner glow */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-400/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Timeline End Cap */}
          <motion.div
            className="relative pl-12 md:pl-24 mt-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="absolute left-0 md:left-8 transform -translate-x-1/2">
              <div className="w-4 h-4 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full border-2 border-gray-900 shadow-lg shadow-cyan-500/30"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
